import React from "react";
import { LightThemeTable } from "components/LightThemeTable";
import CommonModal from "components/CommonModal";
import axios from "axios";
import Config from "config";
import { createRequestAuditLog } from "state/request_details_dependencies";
import { connect, ConnectedProps } from "react-redux";
import { State } from "state";
import Loader from "components/Loader";
import Constants from "helpers/constants";

interface UserRow {
    user_email?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    user_role?: string;
    company_representing?: string;
    title?: string;
    employer?: string;
    region?: string;
    wholesaler_numbers?: string;
    iris_access?: string;
    iris_email?: string;
}

interface UserOnboardingProps extends PropsFromRedux {
    data: any;
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void
}

interface UserOnboardingState {
    users: UserRow[];
    isConfirmModalOpen: boolean;
    isSubmitting: boolean;
}

const styles = {
    confirmMessage: {
        fontSize: "13px",
        color: "#555",
        lineHeight: 1.6,
        marginBottom: "8px",
    } as React.CSSProperties,
    userCount: {
        display: "inline-block",
        backgroundColor: "#FFF7E6",
        border: "1px solid #FFC266",
        color: "#7A4E00",
        borderRadius: "10px",
        padding: "2px 10px",
        fontWeight: 600,
        fontSize: "13px",
    } as React.CSSProperties,
    buttonsContainer: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "12px",
        marginTop: "8px",
    } as React.CSSProperties,
    divider: {
        borderBottom: "1px solid #F0F2F5",
        marginBottom: "16px",
    } as React.CSSProperties,
};

class UserOnboarding extends React.Component<UserOnboardingProps, UserOnboardingState> {

    constructor(props: UserOnboardingProps) {
        super(props);
        this.state = {
            users: props.data?.onboardUserData || [],
            isConfirmModalOpen: false,
            isSubmitting: false,
        };
    }

    componentDidUpdate(prevProps: UserOnboardingProps): void {
        if (prevProps.data?.onboardUserData !== this.props.data?.onboardUserData) {
            this.setState({ users: this.props.data?.onboardUserData || [] });
        }
    }

    generateUserCSV = (users: UserRow[]): File => {
        const headers = [
            "Phone",
            "User Role",
            "Email Address",
            "First Name",
            "Last Name",
            "Company Representing",
            "Title",
            "Employer",
            "Region",
            "Wholesaler Numbers",
            "IRIS Access",
            "IRIS Email",
        ];

        const csvRows = users.map((user) =>
            [
                `"${user.phone ?? ""}"`,
                `"${user.user_role ?? ""}"`,
                `"${user.user_email ?? ""}"`,
                `"${user.first_name ?? ""}"`,
                `"${user.last_name ?? ""}"`,
                `"${user.company_representing ?? ""}"`,
                `"${user.title ?? ""}"`,
                `"${user.employer ?? ""}"`,
                `"${user.region ?? ""}"`,
                `"${user.wholesaler_numbers ?? ""}"`,
                `"${user.iris_access ?? ""}"`,
                `"${user.iris_email ?? ""}"`,
            ].join(",")
        );

        const csvContent = [headers.join(","), ...csvRows].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        return new File([blob], "Users.csv", {
            type: "text/csv",
            lastModified: Date.now(),
        });
    };

    // Step 1: "Onboard User" button clicked → open confirm modal
    handleOnboardClick = (): void => {
        this.setState({ isConfirmModalOpen: true });
    };

    // Step 2: User clicked "Cancel" in modal
    handleConfirmCancel = (): void => {
        if (!this.state.isSubmitting) {
            this.setState({ isConfirmModalOpen: false });
            this.props.onClose();
        }
    };

    // Step 3: User confirmed → make the API call
    handleConfirmSubmit = async (): Promise<void> => {
        const { users } = this.state;
        const { data } = this.props;
        this.setState({ isSubmitting: true });

        try {
            const csvFile = this.generateUserCSV(users);
            const formData = new FormData();
            formData.append("file", csvFile);
            await axios.post(`${Config.API_SERVICE_URL}/api/users`, formData);
            await this.props.createRequestAuditLog(
                data.request_id,
                formData,
                Constants.REQUEST_TASK_USER_ONBOARDED,
                data?.approval_stage_id
            );
            this.props.onClose();
            this.props.onSuccess?.();
            // Success — close modal
            this.setState({ isConfirmModalOpen: false });
        } catch (error) {
            console.error("Onboarding failed:", error);
        } finally {
            this.setState({ isSubmitting: false });
        }
    };

    render(): React.ReactNode {
        const { isOpen } = this.props;
        const { users, isConfirmModalOpen, isSubmitting } = this.state;

        const columns = [
            {
                field: "first_name",
                header: "First Name",
                formatter: (_val: any, row: UserRow) => (
                    <div style={{ padding: "8px" }}>{row.first_name ?? "—"}</div>
                ),
            },
            {
                field: "last_name",
                header: "Last Name",
                formatter: (_val: any, row: UserRow) => (
                    <div style={{ padding: "8px" }}>{row.last_name ?? "—"}</div>
                ),
            },
            {
                field: "user_email",
                header: "Email Address",
                formatter: (_val: any, row: UserRow) => (
                    <div style={{ padding: "8px" }}>{row.user_email ?? "—"}</div>
                ),
            },
        ];

        const modalFooter = (
            <div style={{ ...styles.buttonsContainer, display: 'flex', alignItems: 'center' }}>
                <button
                    className="primary-button"
                    onClick={this.handleConfirmCancel}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <div style={{ display: 'flex', marginLeft: '5px', marginRight: '5px' }}>
                    {isSubmitting && <Loader height={20} width={20} custom={true} />}
                </div>
                <button
                    className="primary-button"
                    onClick={this.handleConfirmSubmit}
                    disabled={isSubmitting}
                    style={{
                        opacity: isSubmitting ? 0.5 : 1,
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                    }}
                >
                    {isSubmitting ? "Onboarding..." : "Onboard"}
                </button>
            </div>
        );

        return (
            <div style={{ padding: "24px" }}>
                {/* Confirmation Modal */}
                <CommonModal
                    show={isOpen}
                    onClose={this.handleConfirmCancel}
                    title=""
                    subTitle=""
                    width="720px"
                    closeOnBackdropClick={!isSubmitting}
                    footer={modalFooter}
                >
                    {/* Modal Header */}
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                        <h2 style={{ fontWeight: 500, fontSize: "16px", margin: 0 }}>
                            User(s) Onboarding
                        </h2>
                    </div>
                    {/* Modal Body */}
                    <p style={styles.confirmMessage}>
                        You are about to onboard{" "}
                        <span style={styles.userCount}>
                            {users.length} user{users.length !== 1 ? "s" : ""}
                        </span>
                        . This will create their accounts and send them access.
                    </p>

                    <div style={styles.divider} />
                    <LightThemeTable
                        data={users}
                        columns={columns}
                        pageSizeOptions={[5, 10, 20]}
                        isLoading={false}
                    />
                </CommonModal>
            </div>
        );
    }
}

const connector = connect(
    (state: State) => ({}),
    {
        createRequestAuditLog,
    }
);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(UserOnboarding);